import { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import axios from 'axios';
import { Layout } from '../components/Layout';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import { Calendar } from '../components/ui/calendar';
import { Popover, PopoverContent, PopoverTrigger } from '../components/ui/popover';
import { format } from 'date-fns';
import { fr } from 'date-fns/locale';
import { CalendarIcon, Save, X, Upload, FileIcon, Trash2, AlertCircle, FolderKanban, FileText, FileSpreadsheet, Presentation, FileArchive } from 'lucide-react';
import { toast } from 'sonner';
import { cn } from '../lib/utils';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const INTERVENTION_TYPES = [
  'Formation',
  'Audit',
  'Conseil',
  'Conseil stratégique',
  'Amélioration des processus',
  'Implémentation technologique',
  'Conseil financier',
  'Conseil RH',
  'Conseil marketing',
  'Autre'
];

const ALLOWED_EXTENSIONS = ['.pdf', '.docx', '.xlsx', '.pptx', '.zip'];
const MAX_FILE_SIZE = 10 * 1024 * 1024; // 10 MB

const getFileIcon = (filename) => {
  const ext = filename.split('.').pop()?.toLowerCase();
  switch (ext) {
    case 'pdf':
      return <FileText className="w-4 h-4 text-red-500" />;
    case 'docx':
    case 'doc':
      return <FileText className="w-4 h-4 text-blue-500" />;
    case 'xlsx':
    case 'xls':
      return <FileSpreadsheet className="w-4 h-4 text-green-500" />;
    case 'pptx':
    case 'ppt':
      return <Presentation className="w-4 h-4 text-orange-500" />;
    case 'zip':
      return <FileArchive className="w-4 h-4 text-yellow-600" />;
    default:
      return <FileIcon className="w-4 h-4 text-slate-500" />;
  }
};

const formatFileSize = (bytes) => {
  if (bytes < 1024) return `${bytes} o`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} Ko`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} Mo`;
};

export const InterventionForm = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const isEdit = Boolean(id);

  const [formData, setFormData] = useState({
    assignment_id: '',
    sme_company_id: '',
    sme_company_name: '',
    intervention_type: '',
    date: '',
    duration_hours: '',
    description: '',
    notes: ''
  });
  const [selectedDate, setSelectedDate] = useState(null);
  const [assignments, setAssignments] = useState([]);
  const [companies, setCompanies] = useState([]);
  const [loading, setLoading] = useState(false);
  const [attachments, setAttachments] = useState([]);
  const [pendingFiles, setPendingFiles] = useState([]); // Files to upload on create
  const [uploading, setUploading] = useState(false);

  useEffect(() => {
    fetchAssignments();
    if (isEdit) {
      fetchIntervention();
    }
  }, [id]);

  const fetchAssignments = async () => {
    try {
      const response = await axios.get(`${API}/assignments`);
      const activeAssignments = response.data.filter(a => a.status === 'active');
      setAssignments(activeAssignments);
      
      const companiesRes = await axios.get(`${API}/sme-companies`);
      setCompanies(companiesRes.data);
    } catch (error) {
      console.error('Erreur lors du chargement des affectations:', error);
    }
  };

  const fetchIntervention = async () => {
    try {
      const response = await axios.get(`${API}/interventions/${id}`);
      const data = response.data;
      setFormData({
        assignment_id: data.assignment_id || '',
        sme_company_id: data.sme_company_id,
        sme_company_name: data.sme_company_name,
        intervention_type: data.intervention_type,
        date: data.date,
        duration_hours: data.duration_hours.toString(),
        description: data.description,
        notes: data.notes || ''
      });
      if (data.date) {
        setSelectedDate(new Date(data.date));
      }
      setAttachments(data.attachments || []);
    } catch (error) {
      toast.error('Erreur lors du chargement de l\'intervention');
      navigate('/consultant/interventions');
    }
  };

  const handleAssignmentChange = (assignmentId) => {
    const assignment = assignments.find(a => a.id === assignmentId);
    if (assignment) {
      setFormData({
        ...formData,
        assignment_id: assignmentId,
        sme_company_id: assignment.sme_company_id,
        sme_company_name: assignment.sme_company_name
      });
    }
  };

  const handleDateSelect = (date) => {
    if (!date) return;
    setSelectedDate(date);
    setFormData({
      ...formData,
      date: format(date, 'yyyy-MM-dd')
    });
  };

  const validateFile = (file) => {
    const ext = '.' + file.name.split('.').pop()?.toLowerCase();
    if (!ALLOWED_EXTENSIONS.includes(ext)) {
      toast.error(`Type de fichier non autorisé. Formats acceptés: ${ALLOWED_EXTENSIONS.join(', ')}`);
      return false;
    }
    if (file.size > MAX_FILE_SIZE) {
      toast.error('Fichier trop volumineux (max 10 Mo)');
      return false;
    }
    return true;
  };

  const handleFileSelect = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    if (!validateFile(file)) {
      e.target.value = '';
      return;
    }

    if (isEdit) {
      // Upload immediately for existing intervention
      handleFileUpload(file);
    } else {
      // Store for later upload after intervention creation
      setPendingFiles([...pendingFiles, file]);
    }
    e.target.value = '';
  };

  const handleRemovePendingFile = (index) => {
    setPendingFiles(pendingFiles.filter((_, i) => i !== index));
  };

  const handleFileUpload = async (file) => {
    if (!id) return;

    setUploading(true);
    const formDataFile = new FormData();
    formDataFile.append('file', file);

    try {
      await axios.post(`${API}/interventions/${id}/attachments`, formDataFile, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      toast.success('Fichier téléchargé');
      fetchIntervention();
    } catch (error) {
      const message = error.response?.data?.detail || 'Erreur lors du téléchargement';
      toast.error(message);
    } finally {
      setUploading(false);
    }
  };

  const uploadPendingFiles = async (interventionId) => {
    for (const file of pendingFiles) {
      const formDataFile = new FormData();
      formDataFile.append('file', file);
      try {
        await axios.post(`${API}/interventions/${interventionId}/attachments`, formDataFile, {
          headers: { 'Content-Type': 'multipart/form-data' }
        });
      } catch (error) {
        console.error(`Erreur upload ${file.name}:`, error);
        toast.error(`Erreur lors de l'upload de ${file.name}`);
      }
    }
  };

  const handleDeleteAttachment = async (attachmentId) => {
    try {
      await axios.delete(`${API}/interventions/${id}/attachments/${attachmentId}`);
      toast.success('Pièce jointe supprimée');
      fetchIntervention();
    } catch (error) {
      toast.error('Erreur lors de la suppression');
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.assignment_id && !isEdit) {
      toast.error('Veuillez sélectionner une affectation');
      return;
    }

    if (!formData.intervention_type || !formData.date || !formData.duration_hours || !formData.description) {
      toast.error('Veuillez remplir tous les champs obligatoires');
      return;
    }

    setLoading(true);

    try {
      if (isEdit) {
        const payload = {
          sme_company_id: formData.sme_company_id,
          sme_company_name: formData.sme_company_name,
          intervention_type: formData.intervention_type,
          date: formData.date,
          duration_hours: parseFloat(formData.duration_hours),
          description: formData.description,
          notes: formData.notes
        };
        await axios.put(`${API}/interventions/${id}`, payload);
        toast.success('Intervention mise à jour avec succès');
      } else {
        const payload = {
          assignment_id: formData.assignment_id,
          intervention_type: formData.intervention_type,
          date: formData.date,
          duration_hours: parseFloat(formData.duration_hours),
          description: formData.description,
          notes: formData.notes
        };
        const response = await axios.post(`${API}/interventions/with-assignment`, payload);
        const newInterventionId = response.data.id;
        
        // Upload pending files
        if (pendingFiles.length > 0) {
          toast.info(`Upload de ${pendingFiles.length} fichier(s)...`);
          await uploadPendingFiles(newInterventionId);
        }
        
        toast.success('Intervention créée avec succès');
      }
      navigate('/consultant/interventions');
    } catch (error) {
      const message = error.response?.data?.detail || 'Erreur lors de l\'enregistrement';
      toast.error(message);
    } finally {
      setLoading(false);
    }
  };

  const groupedAssignments = assignments.reduce((groups, assignment) => {
    const projectName = assignment.project_name || 'Sans projet';
    if (!groups[projectName]) {
      groups[projectName] = [];
    }
    groups[projectName].push(assignment);
    return groups;
  }, {});

  const selectedAssignment = assignments.find(a => a.id === formData.assignment_id);

  return (
    <Layout>
      <div className="max-w-2xl mx-auto space-y-6 fade-in" data-testid="intervention-form-page">
        <div>
          <h1 className="text-3xl font-bold text-slate-900 font-['Plus_Jakarta_Sans']">
            {isEdit ? 'Modifier l\'intervention' : 'Nouvelle intervention'}
          </h1>
          <p className="text-slate-500 mt-1">
            {isEdit ? 'Mettez à jour les détails de l\'intervention' : 'Enregistrez une nouvelle intervention de conseil'}
          </p>
        </div>

        {/* Alert if no assignments */}
        {!isEdit && assignments.length === 0 && (
          <Card className="border-orange-200 bg-orange-50">
            <CardContent className="p-4 flex items-center gap-3">
              <AlertCircle className="w-5 h-5 text-orange-500" />
              <div>
                <p className="text-orange-800 font-medium">Aucune affectation active</p>
                <p className="text-orange-600 text-sm">
                  Vous devez avoir des affectations actives pour créer des interventions. Contactez votre administrateur.
                </p>
              </div>
            </CardContent>
          </Card>
        )}

        <Card className="border-slate-200 shadow-sm">
          <CardContent className="p-6">
            <form onSubmit={handleSubmit} className="space-y-6">
              {/* Assignment Selection (only for new interventions) */}
              {!isEdit && (
                <div className="space-y-2">
                  <Label className="text-slate-700 font-medium">Affectation (Projet + PME) *</Label>
                  {assignments.length > 0 ? (
                    <>
                      <Select 
                        value={formData.assignment_id} 
                        onValueChange={handleAssignmentChange}
                      >
                        <SelectTrigger data-testid="assignment-select">
                          <SelectValue placeholder="Sélectionnez une affectation" />
                        </SelectTrigger>
                        <SelectContent>
                          {Object.entries(groupedAssignments).map(([projectName, projectAssignments]) => (
                            <div key={projectName}>
                              <div className="px-2 py-1.5 text-xs font-semibold text-slate-500 bg-slate-50 flex items-center gap-1">
                                <FolderKanban className="w-3 h-3" />
                                {projectName}
                              </div>
                              {projectAssignments.map((assignment) => (
                                <SelectItem key={assignment.id} value={assignment.id}>
                                  <span className="flex items-center gap-2">
                                    <span>{assignment.sme_company_name}</span>
                                    <span className="text-xs text-slate-400">
                                      (Obj: {assignment.min_hours}h / {assignment.min_interventions} int.)
                                    </span>
                                  </span>
                                </SelectItem>
                              ))}
                            </div>
                          ))}
                        </SelectContent>
                      </Select>
                      
                      {selectedAssignment && (
                        <div className="mt-2 p-3 bg-blue-50 rounded-lg text-sm">
                          <div className="flex items-center gap-2 text-blue-800 font-medium">
                            <FolderKanban className="w-4 h-4" />
                            {selectedAssignment.project_name}
                          </div>
                          <div className="text-blue-600 mt-1">
                            PME: {selectedAssignment.sme_company_name}
                          </div>
                          <div className="text-blue-600 text-xs mt-1">
                            Objectifs: {selectedAssignment.min_hours}h min. / {selectedAssignment.min_interventions} interventions min.
                          </div>
                          <div className="text-blue-600 text-xs">
                            Progression: {selectedAssignment.achieved_hours.toFixed(1)}h / {selectedAssignment.validated_interventions} int. validées
                          </div>
                        </div>
                      )}
                    </>
                  ) : (
                    <Input
                      value="Aucune affectation disponible"
                      disabled
                      className="bg-slate-100"
                    />
                  )}
                  <p className="text-xs text-slate-500">
                    Seules vos affectations actives sont affichées
                  </p>
                </div>
              )}

              {/* For edit mode, show the company (read-only) */}
              {isEdit && (
                <div className="space-y-2">
                  <Label className="text-slate-700 font-medium">Entreprise (PME)</Label>
                  <Input
                    value={formData.sme_company_name}
                    disabled
                    className="bg-slate-100"
                  />
                </div>
              )}

              {/* Intervention Type */}
              <div className="space-y-2">
                <Label className="text-slate-700 font-medium">Type d'intervention *</Label>
                <Select 
                  value={formData.intervention_type} 
                  onValueChange={(value) => setFormData({ ...formData, intervention_type: value })}
                >
                  <SelectTrigger data-testid="type-select">
                    <SelectValue placeholder="Sélectionnez le type" />
                  </SelectTrigger>
                  <SelectContent>
                    {INTERVENTION_TYPES.map((type) => (
                      <SelectItem key={type} value={type}>
                        {type}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              {/* Date */}
              <div className="space-y-2">
                <Label className="text-slate-700 font-medium">Date *</Label>
                <Popover>
                  <PopoverTrigger asChild>
                    <Button
                      variant="outline"
                      className={cn(
                        "w-full justify-start text-left font-normal",
                        !selectedDate && "text-muted-foreground"
                      )}
                      data-testid="date-picker-trigger"
                    >
                      <CalendarIcon className="mr-2 h-4 w-4" />
                      {selectedDate && !isNaN(selectedDate.getTime()) ? format(selectedDate, "d MMMM yyyy", { locale: fr }) : "Sélectionnez une date"}
                    </Button>
                  </PopoverTrigger>
                  <PopoverContent className="w-auto p-0" align="start">
                    <Calendar
                      mode="single"
                      selected={selectedDate}
                      onSelect={handleDateSelect}
                      locale={fr}
                      initialFocus
                    />
                  </PopoverContent>
                </Popover>
              </div>

              {/* Duration */}
              <div className="space-y-2">
                <Label className="text-slate-700 font-medium">Durée (heures) *</Label>
                <Input
                  type="number"
                  step="0.5"
                  min="0.5"
                  placeholder="ex: 2.5"
                  value={formData.duration_hours}
                  onChange={(e) => setFormData({ ...formData, duration_hours: e.target.value })}
                  required
                  data-testid="duration-input"
                />
              </div>

              {/* Description */}
              <div className="space-y-2">
                <Label className="text-slate-700 font-medium">Description *</Label>
                <Textarea
                  placeholder="Décrivez les activités réalisées..."
                  value={formData.description}
                  onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                  required
                  rows={4}
                  data-testid="description-input"
                />
              </div>

              {/* Notes */}
              <div className="space-y-2">
                <Label className="text-slate-700 font-medium">Notes supplémentaires</Label>
                <Textarea
                  placeholder="Observations ou remarques..."
                  value={formData.notes}
                  onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                  rows={3}
                  data-testid="notes-input"
                />
              </div>

              {/* Attachments - Now available for both create and edit */}
              <div className="space-y-2">
                <Label className="text-slate-700 font-medium">Pièces jointes</Label>
                <div className="space-y-2">
                  {/* Existing attachments (edit mode) */}
                  {attachments.map((attachment) => (
                    <div 
                      key={attachment.id} 
                      className="flex items-center justify-between p-3 bg-slate-50 rounded-lg"
                    >
                      <div className="flex items-center gap-2">
                        {getFileIcon(attachment.filename)}
                        <span className="text-sm text-slate-700">{attachment.filename}</span>
                        <span className="text-xs text-slate-400">
                          ({formatFileSize(attachment.size)})
                        </span>
                      </div>
                      <Button
                        type="button"
                        variant="ghost"
                        size="sm"
                        onClick={() => handleDeleteAttachment(attachment.id)}
                        className="text-red-600 hover:text-red-700"
                      >
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </div>
                  ))}
                  
                  {/* Pending files (create mode) */}
                  {pendingFiles.map((file, index) => (
                    <div 
                      key={index} 
                      className="flex items-center justify-between p-3 bg-blue-50 rounded-lg border border-blue-200"
                    >
                      <div className="flex items-center gap-2">
                        {getFileIcon(file.name)}
                        <span className="text-sm text-slate-700">{file.name}</span>
                        <span className="text-xs text-slate-400">
                          ({formatFileSize(file.size)})
                        </span>
                        <span className="text-xs text-blue-600 font-medium">
                          (sera uploadé)
                        </span>
                      </div>
                      <Button
                        type="button"
                        variant="ghost"
                        size="sm"
                        onClick={() => handleRemovePendingFile(index)}
                        className="text-red-600 hover:text-red-700"
                      >
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </div>
                  ))}
                  
                  {/* Upload zone */}
                  <label className="flex flex-col items-center justify-center gap-2 p-4 border-2 border-dashed border-slate-200 rounded-lg cursor-pointer hover:border-slate-300 hover:bg-slate-50 transition-colors">
                    <Upload className="w-6 h-6 text-slate-400" />
                    <span className="text-sm text-slate-500 text-center">
                      {uploading ? 'Téléchargement...' : 'Cliquez pour ajouter une pièce jointe'}
                    </span>
                    <span className="text-xs text-slate-400">
                      PDF, Word, Excel, PowerPoint, ZIP • Max 10 Mo
                    </span>
                    <input 
                      type="file" 
                      className="hidden" 
                      onChange={handleFileSelect}
                      disabled={uploading}
                      accept=".pdf,.docx,.xlsx,.pptx,.zip"
                      data-testid="file-upload"
                    />
                  </label>
                </div>
              </div>

              {/* Actions */}
              <div className="flex gap-4 pt-4">
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => navigate('/consultant/interventions')}
                  className="flex-1"
                  data-testid="cancel-btn"
                >
                  <X className="w-4 h-4 mr-2" />
                  Annuler
                </Button>
                <Button
                  type="submit"
                  disabled={loading || (!isEdit && assignments.length === 0)}
                  className="flex-1 bg-slate-900 hover:bg-slate-800"
                  data-testid="submit-btn"
                >
                  {loading ? (
                    <span className="flex items-center gap-2">
                      <span className="animate-spin h-4 w-4 border-2 border-white border-t-transparent rounded-full"></span>
                      Enregistrement...
                    </span>
                  ) : (
                    <>
                      <Save className="w-4 h-4 mr-2" />
                      {isEdit ? 'Mettre à jour' : 'Créer'} l'intervention
                    </>
                  )}
                </Button>
              </div>
            </form>
          </CardContent>
        </Card>
      </div>
    </Layout>
  );
};
